/**
 * Route -> Sidebar Section -> Background Image mapping (issue #414)
 *
 * The dashboard background changes automatically depending on which sidebar
 * section the active route belongs to. The section grouping itself is NOT
 * duplicated here — it is derived from `NAV_ITEMS` in `config/navConfig.js`
 * (the single source of truth for the sidebar), so this file only adds the
 * section -> background-id mapping and a small pathname matcher.
 *
 * @module utils/backgroundSection
 */

import { NAV_ITEMS } from '../config/navConfig';

/**
 * Sidebar section name (as used in NAV_ITEMS[].section) -> background id.
 * Sections not listed here (e.g. "Ayuda") and unsectioned items (Dashboard,
 * Mensajes) fall back to DEFAULT_SECTION_BACKGROUND.
 */
const SECTION_TO_BACKGROUND = {
  Operaciones: 'cleanroom',
  Comercial: 'comercial',
  Almacén: 'almacen',
  Administración: 'technical',
};

/** Safe default for routes outside any mapped section (Dashboard, Mensajes, Ayuda, Perfil, Novedades). */
export const DEFAULT_SECTION_BACKGROUND = 'technical';

/**
 * Extra path -> section entries for private routes that exist but aren't
 * sidebar items (e.g. /perfil is only reachable from the navbar dropdown,
 * /bodega is a technician self-service page with no sidebar entry of its
 * own). Issue #425: several real routes under App.js have no NAV_ITEMS
 * entry at all, so without these they silently fell back to the default
 * background instead of their actual section.
 */
const EXTRA_SECTIONS = {
  '/perfil': null, // default background — appearance settings live here
  '/bodega': 'Almacén', // self-service warehouse pickup (#98) — Herramientas family, no sidebar item
  '/solicitudes-materiales': 'Almacén', // legacy top-level alias of /admin/solicitudes-materiales
  // '/admin/mensajes' removed in #448 — the route no longer exists (orphaned
  // second messaging UI, consolidated into /mensajes).
};

/**
 * Build a path -> section lookup table from NAV_ITEMS, carrying forward the
 * last-seen `section` value (sections are only stamped on the first item of
 * each group in navConfig.js). Sorted longest-path-first so a startsWith
 * match always prefers the most specific route.
 */
const buildPathSectionTable = () => {
  const table = [];
  let currentSection = null;

  NAV_ITEMS.forEach((item) => {
    if (item.section) currentSection = item.section;
    table.push({ path: item.path, section: currentSection });
  });

  Object.entries(EXTRA_SECTIONS).forEach(([path, section]) => {
    table.push({ path, section });
  });

  return table.sort((a, b) => b.path.length - a.path.length);
};

const PATH_SECTION_TABLE = buildPathSectionTable();

/**
 * Resolve the sidebar section for a given pathname.
 * @param {string} pathname
 * @returns {string|null} Section name, or null if unmatched/unsectioned.
 */
export const getSectionForPath = (pathname) => {
  if (!pathname) return null;
  const match = PATH_SECTION_TABLE.find(
    ({ path }) => pathname === path || pathname.startsWith(`${path}/`)
  );
  return match ? match.section : null;
};

/**
 * Resolve which background image id ('cleanroom' | 'comercial' | 'almacen' |
 * 'technical') should be shown for a given pathname, following the active
 * sidebar section. Routes outside any mapped section use the safe default.
 * @param {string} pathname
 * @returns {string}
 */
export const getBackgroundForPath = (pathname) => {
  const section = getSectionForPath(pathname);
  return SECTION_TO_BACKGROUND[section] || DEFAULT_SECTION_BACKGROUND;
};

export default getBackgroundForPath;
